<script setup lang="ts">
/**
 * 枚举值 → 中文显示。
 *
 * 后端 module-maintenance.schema 里的 options 是存库用的原值
 * （normal / high / critical、Prototype / Pre-launch / Production 等），
 * 直接渲染就等于让工程师在新建表单里认英文枚举。
 * 只改显示不改值，提交上去的仍是后端认识的那个字符串。
 */
const OPTION_TEXT: Readonly<Record<string, string>> = {
  normal: '普通',
  high: '高',
  critical: '紧急',
  Prototype: '样件（Prototype）',
  'Pre-launch': '试生产（Pre-launch）',
  Production: '量产（Production）',
  date: '按日期',
  batch: '按批次',
  sn: '按序列号',
  file: '文件',
  folder: '文件夹',
};

function optionText(value: string, field?: ModuleField): string {
  // 字段自带的映射优先：项目下拉里存的是内部 ID，给人看的得是「DH25017 · X04-C出风口」
  return field?.optionLabels?.[value] ?? OPTION_TEXT[value] ?? value;
}

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

import { confirmAction } from '@/services/confirm';
import type { ModuleField } from '@/types/domain';

const props = withDefaults(defineProps<{
  open: boolean;
  title: string;
  /** 完整字段定义，按 group 分组渲染 */
  fields: ModuleField[];
  /** 初始值；编辑时传入既有记录展开后的值 */
  initial: Record<string, string>;
  saving?: boolean;
  /** 是否显示文件选择；问题/变更可选，质量文件和交付物可设为必选 */
  fileEnabled?: boolean;
  /** 新建时若必须同时上传文件，由父级传入提示 */
  fileRequired?: boolean;
  /** 纯操作弹窗（例如批量导入、项目向导）可以隐藏表单提交区 */
  submitVisible?: boolean;
  submitLabel?: string;
  /**
   * 哪几组默认收起来：给组名清单，或 `'all'` 表示全部收起。
   *
   * 原来折叠是**隐式**约定——分组名里带「按需填」就折叠。
   * 那等于把显示逻辑塞进业务命名里：再想收一组就得回去改组名，
   * 而组名是给人看的抬头，不该为了折叠去迁就（0824 Tony「还是太长」这轮）。
   * 所以改成显式声明，「按需填」那条老约定保留兼容（问题模块还在用），两者取并集。
   *
   * 折叠只改**默认态**，一格都不删：收起来的组抬头照旧写着组名和项数，
   * 「字段被删了」和「字段收起来了」必须一眼分得开。
   */
  collapsedGroups?: string[] | 'all';
}>(), {
  saving: false,
  fileEnabled: false,
  fileRequired: false,
  submitVisible: true,
  submitLabel: '保存',
});

const emit = defineEmits<{
  close: [];
  submit: [values: Record<string, string>, file: File | null];
  /** 某个下拉选定了值；外层据此联动其他字段（如选人后带出姓名和邮箱） */
  'field-picked': [key: string, value: string];
}>();

const form = ref<Record<string, string>>({});
const fileInput = ref<HTMLInputElement | null>(null);
/** 拖拽上传：把 drop 进来的文件塞进原生 input，后面的提交逻辑一行都不用改 */
const dragOver = ref(false);
function onDrop(event: DragEvent): void {
  dragOver.value = false;
  const dropped = event.dataTransfer?.files;
  if (!dropped?.length || !fileInput.value) return;
  const bag = new DataTransfer();
  for (const file of dropped) bag.items.add(file);
  fileInput.value.files = bag.files;
}
const missing = ref<string[]>([]);

/** 打开时的快照；关闭前拿它对比出「有没有没保存的改动」 */
const pristine = ref('');

/**
 * 长下拉改成「打字即筛」。
 *
 * 责任人这类几十上百项的下拉，滚着找是试用反馈里点名的痛点。
 * 8 项以内的（优先级、是否类）保持原生下拉——打字反而比点两下慢。
 * 输入的值必须能对回某个选项（选项原值或显示名都行），
 * 对不上就退回原值并在字段下提示——自由文本混进枚举字段是脏数据的入口。
 */
const comboText = ref<Record<string, string>>({});
const comboBad = ref<Record<string, boolean>>({});

function searchable(field: ModuleField): boolean {
  if (!(field.type === 'select' || Boolean(field.options?.length))) return false;
  // 允许自由填的字段一律走可打字的输入框：纯 <select> 根本没地方填选项外的值
  if (field.allowFreeText) return true;
  return (field.options?.length ?? 0) >= 8;
}

function commitCombo(field: ModuleField): void {
  const typed = (comboText.value[field.key] ?? '').trim();
  if (!typed) {
    form.value[field.key] = '';
    comboBad.value[field.key] = false;
    return;
  }
  const matched = (field.options ?? []).find(
    (option) => option === typed || optionText(option, field) === typed,
  );
  if (matched === undefined && field.allowFreeText) {
    // 选项里没有也照收：BOM 里还没有的零件号，不该因此填不进问题
    form.value[field.key] = typed;
    comboBad.value[field.key] = false;
    emit('field-picked', field.key, typed);
    return;
  }
  if (matched === undefined) {
    /*
     * 对不上就标红提示，但保留人刚敲的字、也不动已存的值。
     * 刻意不把输入框回退成原值：一是打了半天字被无声抹掉很挫败，
     * 二是回退后的文本本身是合法选项，浏览器补发的第二次 change
     * 会把它判成有效输入，刚亮起的错误提示又被清掉（实测踩过）。
     */
    comboBad.value[field.key] = true;
    return;
  }
  form.value[field.key] = matched;
  comboText.value[field.key] = optionText(matched, field);
  comboBad.value[field.key] = false;
  emit('field-picked', field.key, matched);
}
/**
 * 把活表单暴露给父组件（AI 追问 5Why 这类操作区按钮要读写字段）。
 * setValues 刻意不动 pristine：AI 填进来的内容算未保存改动，
 * 关窗时该拦住确认，不能无声丢掉。
 */
defineExpose({
  getValues: (): Record<string, string> => ({ ...form.value }),
  setValues: (patch: Record<string, string>): void => {
    for (const [key, value] of Object.entries(patch)) form.value[key] = value;
  },
  /**
   * 表单有没有还没保存的改动。
   *
   * 抽屉里子条目的动作（8D 步骤、变更措施的「完成」）改的是库，
   * 而人刚在上面填的字段还只在浏览器里——后端当然看不到。
   * 试用反馈里那条「填完日期等信息仍不能提交断点完成」就是这么来的：
   * 实际断点明明在屏幕上写着，报错却说它是空的（2026-08-19）。
   * 让调用方能问一句「脏了吗」，就能在动手之前先把表单存了。
   */
  isDirty: (): boolean => JSON.stringify(form.value) !== pristine.value
    || progressAppend.value.trim() !== '',
});
watch(() => [props.open, props.initial], () => {
  if (!props.open) return;
  const next: Record<string, string> = {};
  for (const field of props.fields) next[field.key] = props.initial[field.key] ?? '';
  form.value = next;
  missing.value = [];
  pristine.value = JSON.stringify(next);
  comboText.value = Object.fromEntries(
    props.fields
      .filter((field) => searchable(field))
      .map((field) => [field.key, optionText(next[field.key] ?? '', field)]),
  );
  comboBad.value = {};
}, { immediate: true, deep: true });


/**
 * 关闭前拦一下：问题/变更这类表单二十多个字段，填到一半误点遮罩
 * 直接丢掉是最伤人的交互。快照对比而不是「碰过就算脏」——
 * 打开看一眼没改任何东西的关闭不该被打扰。
 */
async function requestClose(): Promise<void> {
  if (props.saving) return;
  const dirty = JSON.stringify(form.value) !== pristine.value
    || progressAppend.value.trim() !== ''
    || Boolean(fileInput.value?.files?.length);
  if (dirty) {
    const confirmed = await confirmAction({
      title: '有未保存的修改，确认关闭？',
      message: '关闭后本次填写的内容会丢失。',
      danger: true,
      confirmLabel: '关闭不保存',
    });
    if (!confirmed) return;
  }
  emit('close');
}

/**
 * 按字段自带的 group 分组。
 * 侧边栏那张通用小表单只有 11 个输入框，而问题管理有 23 个字段、变更管理 19 个——
 * 挤在 300px 宽的边栏里根本填不下，所以这里铺成弹窗多列布局。
 */
/** 这次会话里人手动点开过哪几组（关掉弹窗再打开仍算数：那是人自己的选择，不该替他收回去） */
const openGroups = ref(new Set<string>());
/** 老约定：名字里带「按需填」的分组默认收起来。问题模块的 7 组 38 项还靠它，不许回归 */
function isOptional(name: string): boolean {
  return name.includes('按需填');
}
/**
 * 这一组默认收不收起来 = 显式的 collapsedGroups ∪ 隐式的「按需填」。
 *
 * 取并集而不是二选一：新口径（外层显式声明）和老约定同时生效，
 * 变更传 'all' 的同时，问题模块什么都不传也照旧折叠它的冷字段组。
 */
function isFolded(name: string): boolean {
  if (props.collapsedGroups === 'all') return true;
  if (Array.isArray(props.collapsedGroups) && props.collapsedGroups.includes(name)) return true;
  return isOptional(name);
}
function toggleGroup(name: string): void {
  const next = new Set(openGroups.value);
  if (next.has(name)) next.delete(name);
  else next.add(name);
  openGroups.value = next;
}

const groups = computed(() => {
  const result: Array<{ name: string; fields: ModuleField[] }> = [];
  for (const field of props.fields) {
    const name = field.group || '基本信息';
    const group = result.find((item) => item.name === name);
    if (group) group.fields.push(field);
    else result.push({ name, fields: [field] });
  }
  return result;
});

/* multi 字段：串 ↔ 勾选 的换算。分隔符没有斜杠——选项名本身带「/」 */
function multiParts(key: string): string[] {
  return String(form.value[key] ?? '').split(/[、,，;；]+/u).map((v) => v.trim()).filter(Boolean);
}
function multiHas(key: string, option: string): boolean {
  return multiParts(key).includes(option);
}
function toggleMulti(key: string, option: string): void {
  const parts = multiParts(key);
  const at = parts.indexOf(option);
  if (at >= 0) parts.splice(at, 1); else parts.push(option);
  form.value[key] = parts.join('、');
}

/** 缺了哪些必填字段的 key；用来把输入框标红并滚过去 */
const missingKeys = ref<string[]>([]);

function submit() {
  // 必填项一次性全部标出来，不要让人改一个提交一次
  const gaps = props.fields
    .filter((field) => field.required && !(form.value[field.key] ?? '').trim());
  missingKeys.value = gaps.map((field) => field.key);
  missing.value = gaps.map((field) => field.label);
  if (props.fileRequired && !fileInput.value?.files?.[0]) {
    missing.value.push('业务文件');
  }
  if (missing.value.length) {
    /*
      缺项提示条在抽屉最顶上，而人是在最底下点的「保存」——
      表单二三十个字段，提示条在视口外，点下去像**什么都没发生**
      （0820 P8「无法保存」的真实体感：填了根因和措施，却因为
      「问题发现时间」空着被拦，而那 26 条迁移问题本来就没有这个值）。
      所以除了标红，还得把第一个缺的字段滚进视野。
    */
    void nextTick(() => {
      const first = missingKeys.value[0];
      const target = first
        ? document.querySelector(`[data-field="${typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(first) : first}"]`)
        : document.querySelector('.dialog-missing');
      target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      (target?.querySelector('input, select, textarea') as HTMLElement | null)?.focus();
    });
    return;
  }
  missingKeys.value = [];
  const payload = { ...form.value };
  // 只读字段不送回去：值由系统定（问题提出人，0928），送回去只会被后端当成「想改」
  for (const field of props.fields) if (field.readonly) delete payload[field.key];
  if (PROGRESS_KEY in payload) payload[PROGRESS_KEY] = mergedProgress();
  emit('submit', payload, fileInput.value?.files?.[0] ?? null);
  progressAppend.value = '';
}

/**
 * 「最新进展」是累积日志，不是一句话状态。
 *
 * 旧 EQS 里这一列长这样：
 *   2026.7.1: 计划7/6前设计出方案，7/17前完成打样
 *   2026.6.24: 已收到样件，颜色偏深
 * 每次跟进都往上加一条，历史留着——这正是问题清单最有价值的部分。
 *
 * 所以不给一个大 textarea 让人自己维护格式（那样迟早被整段覆盖掉），
 * 而是给一个「追加」输入框，提交时自动加日期前缀、压到最前面，旧内容只读展示。
 */
const PROGRESS_KEY = 'progressLog';
const progressAppend = ref('');

const progressHistory = computed(() =>
  String(form.value[PROGRESS_KEY] ?? '')
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .filter(Boolean));

function stampToday(): string {
  const now = new Date(Date.now() + 8 * 60 * 60 * 1000);
  return `${now.getUTCFullYear()}.${now.getUTCMonth() + 1}.${now.getUTCDate()}`;
}

/** 把待追加的内容并进日志；没填就原样返回，不会凭空写一条空进展 */
function mergedProgress(): string {
  const next = progressAppend.value.trim();
  if (!next) return String(form.value[PROGRESS_KEY] ?? '');
  const entry = /^\d{4}[.\-/]/u.test(next) ? next : `${stampToday()}: ${next}`;
  return [entry, ...progressHistory.value].join('\n');
}

function onKey(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.open && !props.saving) void requestClose();
}
onMounted(() => window.addEventListener('keydown', onKey));
onUnmounted(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <div v-if="open" class="record-dialog-mask" @click.self="requestClose">
    <section class="record-dialog" role="dialog" aria-modal="true">
      <header>
        <div>
          <span>RECORD DETAILS</span>
          <h3>{{ title }}</h3>
        </div>
        <button class="inline-button" type="button" :disabled="saving" @click="requestClose">关闭</button>
      </header>

      <p v-if="missing.length" class="dialog-missing">
        以下必填项还没填：{{ missing.join('、') }}
      </p>

      <!--
        主流程条（变更五步）挂这里：**在所有字段之前**，而且在 <form> 之外。

        ① 顺序：五步是变更的唯一端口，打开一条单子第一眼该看到「走到哪一步、这步归谁」，
           而不是先滚过一屏字段才看见它（0824 Tony「还是太长」——五步原来挂在
           operations 里，排在 24 格字段后面）；
        ② 在 form 外：挂进来的东西自带表单（第 4 步的清单有「＋ 添加」那个 <form>），
           套进外层 <form> 就是 form 嵌 form —— 无效 HTML，里面的按钮还会误触外层提交。
        不传这个插槽就什么都不渲染，别的模块零影响。
      -->
      <div v-if="$slots['before-form']" class="dialog-before-form">
        <slot name="before-form" />
      </div>

      <!--
        没有字段、也不给保存按钮时（变更 0826：填写口全在五步里），form 还得为
        operations 插槽（附件抽屉/状态操作）撑着——按 fields.length 判会把它们一起藏掉。
      -->
      <form v-if="fields.length || submitVisible || $slots.operations || fileEnabled || fileRequired" @submit.prevent="submit">
        <!--
          字段区最上面的一句话。谁把字段摘走了、谁把分组收起来了，谁负责在这里说清楚——
          变更的编号/标题/等级搬进五步表单、其余分组默认收起后，人打开抽屉看到的是一片空缺，
          第一反应是「数据丢了」（0824）。不传这个插槽就什么都不渲染，别的模块不受影响。
        -->
        <p v-if="$slots['form-top']" class="dialog-form-note"><slot name="form-top" /></p>
        <!--
          折叠的两个来源：外层显式点名的 collapsedGroups，和名字带「按需填」的老约定。
          问题模块原来 7 组 38 项一屏铺开，试用反馈「有点繁琐」——
          常用的十来项留在表面，冷字段（市场索赔、追溯、5Why）收进折叠区；
          变更编辑态更进一步，整片收起来（五步接管了填写口）。
          两种情况都**不删字段**：存量数据还在用，抬头写着有几项，点开就有。
        -->
        <fieldset v-for="group in groups" :key="group.name" :class="{ folded: isFolded(group.name) }">
          <legend v-if="isFolded(group.name)">
            <button type="button" class="group-toggle" @click="toggleGroup(group.name)">
              {{ openGroups.has(group.name) ? '▾' : '▸' }} {{ group.name }}
              <em>{{ group.fields.length }} 项</em>
            </button>
          </legend>
          <legend v-else>{{ group.name }}<em>{{ group.fields.length }} 项</em></legend>
          <div v-show="!isFolded(group.name) || openGroups.has(group.name)" class="dialog-fields">
            <label
              v-for="field in group.fields"
              :key="field.key"
              :data-field="field.key"
              :class="{ wide: field.type === 'textarea', 'field-missing': missingKeys.includes(field.key) }"
            >
              <span>{{ field.label }}<b v-if="field.required"> *</b></span>

              <!-- 只读：值由系统定（问题提出人 = 填写人，0928）。新建时还没有值，就说清楚会记成谁 -->
              <template v-if="field.readonly">
                <output class="readonly-value">{{ (form[field.key] ?? '').trim() || '—' }}</output>
              </template>

              <!-- 进展日志：只能追加，不给整段编辑。
                   给个大 textarea 的话，迟早有人把半年的跟进记录一次性覆盖掉。 -->
              <template v-else-if="field.key === PROGRESS_KEY">
                <input
                  v-model="progressAppend"
                  type="text"
                  placeholder="追加一条进展，回车即可（自动加日期前缀）"
                  @keydown.enter.prevent
                >
                <small class="progress-hint">
                  提交后自动记为「{{ stampToday() }}: …」并置顶；不填则保留原有记录。
                </small>
                <ol v-if="progressHistory.length" class="progress-history">
                  <li v-for="(line, index) in progressHistory" :key="index">{{ line }}</li>
                </ol>
                <small v-else class="progress-hint">暂无历史进展。</small>
              </template>

              <!--
                多选（type: 'multi'）：勾选组，值存成「、」连接的一个串。
                表单模型是 Record<string, string>，不为多选改成数组——
                后端 normalized 里也是这个串，解析规则两边一致（按「、,，;；」拆，不拆「/」）。
              -->
              <template v-else-if="field.type === 'multi'">
                <div class="multi-options">
                  <label v-for="option in field.options" :key="option" class="multi-option">
                    <input
                      type="checkbox"
                      :checked="multiHas(field.key, option)"
                      @change="toggleMulti(field.key, option)"
                    >
                    <span>{{ option }}</span>
                  </label>
                </div>
                <!--
                  勾完立刻告诉人「这次会生成哪几条动作」。
                  新建时还没有记录，清单区是空的——人看不到自己勾选的后果
                  （Tony 2026-08-18：「新建里面还是看不到下一步的行动清单」）。
                  内容由父层通过具名插槽给，对话框本身不认识业务。
                -->
                <slot :name="`after-${field.key}`" :value="form[field.key] ?? ''" />
              </template>
              <textarea
                v-else-if="field.type === 'textarea'"
                v-model="form[field.key]"
                rows="4"
                :placeholder="field.help || '可填写多行内容'"
              ></textarea>
              <!-- 长下拉（≥8 项）改成打字即筛的输入框；对不上的输入会退回并提示 -->
              <template v-else-if="searchable(field)">
                <input
                  v-model="comboText[field.key]"
                  type="search"
                  class="combo-input"
                  :class="{ 'combo-bad': comboBad[field.key] }"
                  :list="`record-combo-${field.key}`"
                  placeholder="打字筛选，从建议里选"
                  autocomplete="off"
                  @change="commitCombo(field)"
                  @keydown.enter.prevent="commitCombo(field)"
                >
                <datalist :id="`record-combo-${field.key}`">
                  <option v-for="option in field.options" :key="option" :value="optionText(option, field)" />
                </datalist>
                <small v-if="comboBad[field.key]" class="combo-error">没有这个选项，已退回原值；从下拉建议里选一个。</small>
                <small v-else-if="field.allowFreeText" class="combo-free">建议里没有也可以直接填。</small>
              </template>
              <!-- 只要给了 options 就渲染成下拉。
                   之前必须同时写 type:'select' 才生效，结果像「断点类型」这种
                   已经定义了 date/batch/sn 三个取值的字段，界面上仍是一个自由文本框——
                   选项定义了却没人看得见，还得靠人背着填。 -->
              <select
                v-else-if="field.type === 'select' || field.options?.length"
                v-model="form[field.key]"
                @change="emit('field-picked', field.key, form[field.key] ?? '')"
              >
                <option value="">请选择</option>
                <!-- 值保持后端原样，只翻译显示：新建表单里让人选 normal/high/critical 说不过去 -->
                <option v-for="option in field.options" :key="option" :value="option">{{ optionText(option, field) }}</option>
              </select>
              <input
                v-else
                v-model="form[field.key]"
                :type="field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'"
              />
              <small v-if="field.help && field.type !== 'textarea'">{{ field.help }}</small>
            </label>
          </div>
        </fieldset>

        <fieldset v-if="fileEnabled || fileRequired">
          <legend>业务文件<em>{{ fileRequired ? '必填' : '可选附件' }}</em></legend>
          <div class="dialog-fields">
            <label
              class="wide dropzone" :class="{ 'is-over': dragOver }"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onDrop"
            >
              <span>选择文件<b v-if="fileRequired"> *</b></span>
              <!-- 拖拽上传：现场拍的照片直接从桌面拖进来，不用点两次再翻目录 -->
              <input ref="fileInput" type="file" @change="dragOver = false" />
              <small class="dropzone-hint">
                {{ dragOver ? '松开即可放入' : '点上面选择，或把文件拖到这里' }}
              </small>
              <small v-if="fileRequired">新建时必须同时上传真实业务文件，仅有元数据的记录审批人打不开。</small>
              <small v-else>可上传照片、报告、8D、邮件附件等佐证材料；不上传也可以先保存问题。</small>
            </label>
          </div>
        </fieldset>

        <div v-if="$slots.operations" class="record-dialog-extra">
          <slot name="operations" />
        </div>

        <footer v-if="submitVisible">
          <button class="button button-primary" type="submit" :disabled="saving">
            {{ saving ? '正在保存…' : submitLabel }}
          </button>
          <button class="button" type="button" :disabled="saving" @click="emit('close')">取消</button>
        </footer>
      </form>
      <div v-if="$slots.default" class="record-dialog-extra">
        <slot />
      </div>
    </section>
  </div>
</template>

<style scoped>
.readonly-value { display: block; min-height: 34px; padding: 7px 10px; border: 1px dashed var(--line, #d7dde5); border-radius: 4px; background: var(--soft, #f7f9fb); color: var(--ink, #1f2937); font-size: 13px; }
/*
  勾选组。原来是 inline-flex 自由换行，勾选框在 flex 里没锁尺寸，
  被「安全或法规特性」这种长标签一挤就变形，八个框大小不一（Tony 2026-08-18）。
  改成固定两列栅格：每格宽度一样，框 `flex: 0 0 16px` 锁死，标签再长也不动它。
*/
.multi-options {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 6px 12px; padding: 4px 0;
}
.multi-option {
  display: flex; align-items: center; gap: 7px; min-width: 0;
  font-size: 12px; color: var(--ink); cursor: pointer;
}
.multi-option input {
  flex: 0 0 16px; width: 16px; height: 16px; margin: 0;
  accent-color: var(--blue); cursor: pointer;
}
.multi-option span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* 拖拽区：平时是虚线框，拖到上面变蓝 */
.dropzone { padding: 10px; border: 1px dashed var(--line-strong); border-radius: var(--radius-s); }
.dropzone.is-over { border-color: var(--blue); background: rgba(0, 117, 174, .06); }
.dropzone-hint { color: var(--muted); font-size: 12px; }
/*
  折叠分组：收起时只剩一行标题，不占版面。
  抬头做成**有边框**的小按钮——变更编辑态整片收起来之后，这行字就是人找回字段的唯一入口，
  一行没边框的文字看不出能点，看着就像字段被删了（同「可点的东西必须有边框」那条口径）。
*/
.group-toggle {
  display: inline-flex; align-items: baseline; gap: 6px;
  padding: 3px 9px; border: 1px solid var(--line); border-radius: var(--radius-s);
  background: var(--white); color: var(--ink); font: inherit; cursor: pointer;
}
.group-toggle:hover { border-color: var(--blue); color: var(--blue); }
fieldset.folded { border-style: dashed; }
.progress-hint { color: var(--muted); font-size: 12px; line-height: 1.7; }

.progress-history {
  max-height: 168px;
  overflow-y: auto;
  margin: 6px 0 0;
  padding: 8px 10px 8px 24px;
  border-left: 3px solid var(--blue);
  background: var(--paper);
  color: var(--ink);
  font-size: 12px;
  line-height: 1.8;
}

.progress-history li { margin: 0; }
.progress-history li:first-child { font-weight: 700; }

/* 右侧抽屉：编辑一条记录时左边的列表要一直看得见，
   改完一条顺手点下一条——这是问题/变更/团队这类「列表逐条维护」的主要工作流。
   宽度取 760px：3 列栅格塞不下，但 2 列 + 分组足够，且在 1366px 笔记本上仍能露出半屏列表。 */
.record-dialog-mask { position: fixed; inset: 0; z-index: 60; display: flex; justify-content: flex-end; background: rgba(15, 23, 36, .4); }
.record-dialog { width: min(760px, 92vw); height: 100%; overflow-y: auto; background: var(--white); border-left: 1px solid var(--line); box-shadow: -10px 0 40px rgba(15, 23, 36, .18); animation: drawer-in 180ms ease-out; }
@keyframes drawer-in { from { transform: translateX(24px); opacity: .6; } to { transform: none; opacity: 1; } }
.record-dialog > header { position: sticky; top: 0; z-index: 1; display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; padding: 16px 20px; border-bottom: 1px solid var(--line); background: var(--white); }
.record-dialog header span { color: var(--muted); font: 700 10px var(--display); letter-spacing: .08em; }
.record-dialog header h3 { margin: 2px 0 0; font-size: 17px; }
.dialog-missing { margin: 0; padding: 9px 20px; border-bottom: 1px solid var(--line); background: #fdecea; color: #922; font-size: 12px; }
/* 说明条，不是错误条：用蓝边和 dialog-missing 拉开距离，别让人以为自己填错了什么 */
.dialog-form-note {
  margin: 14px 0 0; padding: 9px 12px;
  border-left: 3px solid var(--blue); background: rgba(0, 117, 174, .06);
  color: var(--ink); font-size: 12px; line-height: 1.7;
}
/* 字段之前的主流程区（变更五步）。和 form 用同一套左右留白，看着是同一列 */
.dialog-before-form { padding: 4px 20px 0; }
.record-dialog form { padding: 4px 20px 16px; }
.record-dialog fieldset { margin: 14px 0 0; padding: 0; border: 0; }
.record-dialog legend { display: flex; align-items: baseline; gap: 8px; padding: 0 0 8px; color: var(--blue); font: 800 12px var(--display); }
.record-dialog legend em { color: var(--muted); font: 400 11px/1 inherit; font-style: normal; }
.dialog-fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 10px 14px; }
.dialog-fields label { display: grid; gap: 3px; min-width: 0; }
.dialog-fields label.wide { grid-column: 1 / -1; }
.dialog-fields span { color: var(--ink); font-size: 12px; font-weight: 700; }
.dialog-fields span b { color: #c0392b; }
.dialog-fields input, .dialog-fields select, .dialog-fields textarea { width: 100%; padding: 6px 8px; border: 1px solid var(--line); background: var(--white); font: inherit; font-size: 12px; }
.dialog-fields textarea { resize: vertical; line-height: 1.7; }
.dialog-fields small { color: var(--muted); font-size: 12px; line-height: 1.5; }
.dialog-fields .combo-bad { border-color: #c0392b; }
/* 被拦下的必填项：标红并且滚到视野里，不能只在顶上写一行字 */
.dialog-fields label.field-missing > span { color: #c0392b; }
.dialog-fields label.field-missing input,
.dialog-fields label.field-missing select,
.dialog-fields label.field-missing textarea { border-color: #c0392b; background: #fdf3f2; }
.dialog-fields .combo-error { color: #c0392b; }
.record-dialog footer { position: sticky; bottom: 0; z-index: 1; display: flex; gap: 8px; margin-top: 18px; padding: 12px 0; border-top: 1px solid var(--line); background: var(--white); }
.record-dialog-extra { padding: 0 20px 20px; }
.record-dialog form > .record-dialog-extra { padding: 0; }
.record-dialog-extra > :first-child { margin-top: 14px; }
.record-dialog-extra :deep(.dialog-operations) { color: var(--ink); }
.record-dialog-extra :deep(.status-actions),
.record-dialog-extra :deep(.file-operations) { margin-top: 14px; padding-top: 16px; border-top-color: var(--line); }
.record-dialog-extra :deep(.status-actions > span),
.record-dialog-extra :deep(.file-operations > span) { color: var(--blue); }
.record-dialog-extra :deep(.status-context),
.record-dialog-extra :deep(.operation-hint),
.record-dialog-extra :deep(.version-list article),
.record-dialog-extra :deep(.version-list small) { color: var(--muted); }
.record-dialog-extra :deep(.status-actions label > span),
.record-dialog-extra :deep(.file-operations label > span) { color: var(--ink); }
.record-dialog-extra :deep(.status-actions input),
.record-dialog-extra :deep(.file-operations input) { border-color: var(--line); color: var(--ink); background: var(--white); }
.record-dialog-extra :deep(.version-list) { border-top-color: var(--line); }

@media (max-width: 900px) {
  .record-dialog { width: 100%; }
  .dialog-fields { grid-template-columns: 1fr; }
}
</style>
